import React, { useState } from 'react';
import { Star, X, CheckCircle2, Sparkles, ShieldCheck } from 'lucide-react';
import { submitReview, CustomerReview } from '../services/reviewService';

interface WriteReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingRef?: string;
  initialCustomerName?: string;
  initialArea?: string;
  initialServices?: string;
  onReviewSubmitted?: (review: CustomerReview) => void;
}

const QUICK_TAGS = [
  '⭐ On-Time Arrival',
  '✨ Super Clean & Hygienic',
  '🌸 Gentle & Low-Pain Waxing',
  '💆 Relaxing Massage',
  '🌟 Radiant Facial Glow',
  '👩‍🦰 Courteous & Polite Beautician',
  '💰 Transparent Pricing'
];

export const WriteReviewModal: React.FC<WriteReviewModalProps> = ({
  isOpen,
  onClose,
  bookingRef,
  initialCustomerName = '',
  initialArea = '',
  initialServices = '',
  onReviewSubmitted
}) => {
  const [rating, setRating] = useState<number>(5);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [customerName, setCustomerName] = useState<string>(initialCustomerName);
  const [area, setArea] = useState<string>(initialArea);
  const [serviceNames, setServiceNames] = useState<string>(initialServices);
  const [reviewText, setReviewText] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  if (!isOpen) return null;

  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reviewText.trim()) return;

    setIsSubmitting(true);
    try {
      // Append selected compliments to review text if not already present
      let finalReview = reviewText.trim();
      if (selectedTags.length > 0) {
        const tagsString = selectedTags.map(t => t.replace(/^[^\w\s]+/, '').trim()).join(', ');
        finalReview += `\n\nHighlights: ${tagsString}`;
      }

      const newReview = await submitReview({
        bookingRef,
        customerName: customerName.trim() || 'Happy Customer',
        rating,
        reviewText: finalReview,
        serviceNames: serviceNames.trim() || undefined,
        area: area.trim() || 'Bhilai / Durg'
      });

      setSubmitted(true);
      if (onReviewSubmitted) {
        onReviewSubmitted(newReview);
      }

      setTimeout(() => {
        onClose();
        setSubmitted(false);
        setReviewText('');
        setSelectedTags([]);
      }, 2200);
    } catch (err) {
      console.error('Failed to submit review:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRatingLabel = (val: number) => {
    switch (val) {
      case 5: return '5 Stars – Excellent, Loved the service! 💖';
      case 4: return '4 Stars – Very Good experience 😊';
      case 3: return '3 Stars – Average, Good 👍';
      case 2: return '2 Stars – Could be better';
      case 1: return '1 Star – Disappointed';
      default: return '';
    }
  };

  return (
    <div className="cart-modal-backdrop" onClick={onClose} style={{ zIndex: 1100 }}>
      <div
        className="cart-modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '480px', maxHeight: '92vh', overflowY: 'auto' }}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="cart-modal-close-btn"
          aria-label="Close review modal"
        >
          <X size={20} />
        </button>

        {submitted ? (
          <div style={{ textAlign: 'center', padding: '2.5rem 1.5rem' }}>
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 1.25rem',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(46, 125, 50, 0.12) 0%, rgba(76, 175, 80, 0.25) 100%)',
              color: '#2e7d32',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CheckCircle2 size={36} />
            </div>
            <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--blishka-berry)', marginBottom: '0.5rem' }}>
              Thank You for Your Feedback!
            </h3>
            <p style={{ fontSize: '0.92rem', color: 'var(--color-text-body)', lineHeight: 1.5, margin: '0 auto 1rem', maxWidth: '340px' }}>
              Your rating & review have been submitted and are now featured on our homepage for women in Bhilai & Durg!
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', color: '#f59e0b' }}>
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} size={22} fill={s <= rating ? '#f59e0b' : 'none'} color="#f59e0b" />
              ))}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ padding: '0.5rem 0.25rem' }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                background: 'rgba(125, 26, 56, 0.08)',
                color: 'var(--blishka-berry)',
                padding: '0.3rem 0.8rem',
                borderRadius: '20px',
                fontSize: '0.78rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '0.5rem'
              }}>
                <Sparkles size={13} />
                <span>Verified Customer Review</span>
              </div>
              <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--color-text-title)', margin: '0 0 0.35rem' }}>
                Rate Your Experience
              </h3>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', margin: 0 }}>
                {bookingRef ? `Booking Ref: ${bookingRef}` : 'Help other women in Bhilai & Durg find top doorstep salon care'}
              </p>
            </div>

            {/* Star Rating Selector */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(125, 26, 56, 0.04) 0%, rgba(245, 158, 11, 0.08) 100%)',
              border: '1px solid rgba(245, 158, 11, 0.25)',
              borderRadius: '16px',
              padding: '1.2rem',
              textAlign: 'center',
              marginBottom: '1.25rem'
            }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--color-text-title)', marginBottom: '0.6rem' }}>
                How would you rate the service quality?
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', cursor: 'pointer' }}>
                {[1, 2, 3, 4, 5].map((starVal) => {
                  const isFilled = (hoverRating || rating) >= starVal;
                  return (
                    <button
                      key={starVal}
                      type="button"
                      onMouseEnter={() => setHoverRating(starVal)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setRating(starVal)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        transform: (hoverRating || rating) === starVal ? 'scale(1.2)' : 'scale(1)',
                        transition: 'transform 0.15s ease'
                      }}
                      title={`${starVal} Star`}
                    >
                      <Star
                        size={34}
                        fill={isFilled ? '#f59e0b' : 'none'}
                        color={isFilled ? '#f59e0b' : '#d1d5db'}
                        strokeWidth={1.75}
                      />
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#d97706', marginTop: '0.5rem', minHeight: '1.2rem' }}>
                {getRatingLabel(hoverRating || rating)}
              </div>
            </div>

            {/* Quick Tag Compliments */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--color-text-title)', marginBottom: '0.45rem' }}>
                What did you like the most? (Optional)
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {QUICK_TAGS.map((tag) => {
                  const isSelected = selectedTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      style={{
                        fontSize: '0.78rem',
                        padding: '0.35rem 0.7rem',
                        borderRadius: '20px',
                        border: isSelected ? '1.5px solid var(--blishka-berry)' : '1px solid rgba(0,0,0,0.1)',
                        background: isSelected ? 'rgba(125, 26, 56, 0.08)' : '#ffffff',
                        color: isSelected ? 'var(--blishka-berry)' : 'var(--color-text-body)',
                        fontWeight: isSelected ? 700 : 500,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Review Comment Textarea */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--color-text-title)', marginBottom: '0.35rem' }}>
                Your Review & Feedback <span style={{ color: 'var(--blishka-berry)' }}>*</span>
              </label>
              <textarea
                rows={4}
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="Share your experience (e.g. How was the beautician's behavior, hygiene, and service results?)"
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '12px',
                  border: '1.5px solid rgba(125, 26, 56, 0.2)',
                  fontSize: '0.88rem',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Customer Details Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.25rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--color-text-title)', marginBottom: '0.25rem' }}>
                  Your Name
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="e.g. Sneha Jain"
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.75rem',
                    borderRadius: '10px',
                    border: '1px solid rgba(0,0,0,0.15)',
                    fontSize: '0.85rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--color-text-title)', marginBottom: '0.25rem' }}>
                  Area / Locality
                </label>
                <input
                  type="text"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  placeholder="e.g. Smriti Nagar, Bhilai"
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.75rem',
                    borderRadius: '10px',
                    border: '1px solid rgba(0,0,0,0.15)',
                    fontSize: '0.85rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Optional Service Name */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--color-text-title)', marginBottom: '0.25rem' }}>
                Service Availed (Optional)
              </label>
              <input
                type="text"
                value={serviceNames}
                onChange={(e) => setServiceNames(e.target.value)}
                placeholder="e.g. Lotus Glow Facial & Underarms Wax"
                style={{
                  width: '100%',
                  padding: '0.6rem 0.75rem',
                  borderRadius: '10px',
                  border: '1px solid rgba(0,0,0,0.15)',
                  fontSize: '0.85rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Verified Badge note */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.78rem',
              color: '#2e7d32',
              background: 'rgba(46, 125, 50, 0.08)',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              marginBottom: '1.25rem'
            }}>
              <ShieldCheck size={16} />
              <span>Your review will appear with a Verified Customer badge on the Blishka homepage.</span>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !reviewText.trim()}
              className="btn btn-berry"
              style={{
                width: '100%',
                padding: '0.85rem',
                fontSize: '0.95rem',
                fontWeight: 700,
                opacity: (!reviewText.trim() || isSubmitting) ? 0.6 : 1,
                cursor: (!reviewText.trim() || isSubmitting) ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? 'Submitting Review...' : 'Submit Rating & Review ✨'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
